"use client";

import { useState } from "react";
import { ChevronUp, Loader2Icon, TrashIcon } from "lucide-react";
import { Stars } from "@/components/common/feedback/feedback-stars";
import ProfilePicture from "@/components/common/profile-picture";
import VerifiedBadgeCheck from "@/components/common/verified-badge-check";
import { TProductReview } from "@/types/productv2";
import { format } from "date-fns";
import { formatImageUrl } from "@/lib/utils";
import ProfileLinkedInIcon from "@/icons/profile-linkedin";
import Link from "next/link";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { useModal } from "@/providers/modal-provider";
import { useAuth } from "@/providers/auth-provider";
import { PRODUCTV2_API } from "@/lib/constants";
import { ModalType } from "@/types/modal-type";

const ReviewCard = ({
	reviews = [],
	error,
	productSlug,
}: {
	reviews?: TProductReview[];
	error: Error | null;
	productSlug: string;
}) => {
	const [expandedReviews, setExpandedReviews] = useState<Record<string, boolean>>({});
	const { user, isLoggedIn } = useAuth();
	const { openModal } = useModal();
	const queryClient = useQueryClient();

	const deleteReviewMutation = useMutation({
		mutationFn: async (reviewId: string) => {
			if (!isLoggedIn) {
				openModal(ModalType.Auth);
				throw new Error("Please login to delete review.");
			}

			const res = await fetch(`${PRODUCTV2_API}/${productSlug}/reviews`, {
				method: "DELETE",
				body: JSON.stringify({ reviewId }),
			});

			if (!res.ok) {
				const err = await res.json();
				throw new Error(err.message || "Failed to delete review");
			}
		},
		onSuccess: () => {
			toast.success("Review deleted successfully");
			queryClient.invalidateQueries({ queryKey: ["reviews", productSlug] });
		},
		onError: (error: unknown) => {
			if (error instanceof Error) {
				toast.error(error.message);
			} else {
				toast.error("Failed to delete review");
			}
		},
	});

	if (error) {
		return <p className="text-red-500">Error fetching reviews: {error.message}</p>;
	}

	if (reviews.length === 0) {
		return <p className="text-sm text-gray-500">No reviews available.</p>;
	}

	const toggleReview = (reviewId: string) => {
		setExpandedReviews((prev) => ({
			...prev,
			[reviewId]: !prev[reviewId],
		}));
	};

	return (
		<div className="space-y-4">
			{reviews.map((review) => {
				const isCreator =
					review.user?.UserProfile?.username === user?.username || review.user?.id === user?.id;
				const canDelete = isCreator || user?.isAdmin;

				return (
					<div key={review.id} className="rounded-lg border bg-brand-primary/[0.5%] p-5 shadow-sm">
						<div className="space-y-3">
							<div className="flex items-center gap-3">
								<Link
									href={
										review.user?.UserProfile?.username
											? `/${review.user?.UserProfile?.username}`
											: `#`
									}
								>
									<ProfilePicture
										profilePictureUrl={formatImageUrl(review.user?.image)}
										alternateText={review.user?.name}
										isVerified={review.user?.UserProfile?.isVerified}
										isFounder={review.user?.UserProfile?.isFounder}
										className={{
											container: "h-10 w-10 rounded-full p-0.5",
											image: "rounded-full object-cover text-gray-600",
											badgeImage: "-bottom-3.5 h-7 w-7",
										}}
									/>
								</Link>
								<div className="flex-1">
									<div className="flex items-center gap-2">
										<p className="font-bold text-dark-primary md:text-lg">{review.user?.name}</p>
										{review.user?.UserProfile?.isVerified && (
											<VerifiedBadgeCheck iconClassName="w-5 h-5" />
										)}
										{review.reviewerDesignation?.linkedin && (
											<Link href={review.reviewerDesignation.linkedin} target="_blank">
												<ProfileLinkedInIcon className="h-4 w-4" />
											</Link>
										)}
									</div>
									{(review.reviewerDesignation?.jobTitle ||
										review.reviewerDesignation?.companyName) && (
										<p className="text-xs text-gray-500">
											{[
												review.reviewerDesignation?.jobTitle,
												review.reviewerDesignation?.companyName,
											]
												.filter(Boolean)
												.join(", ")}
										</p>
									)}
								</div>

								{canDelete && (
									<button
										type="button"
										onClick={() => deleteReviewMutation.mutate(review.id)}
										className="rounded-md border p-2 transition hover:bg-red-500/10 disabled:opacity-60"
										disabled={deleteReviewMutation.isLoading}
									>
										{deleteReviewMutation.isLoading ? (
											<Loader2Icon className="h-4 w-4 animate-spin text-brand-primary" />
										) : (
											<TrashIcon className="h-4 w-4" />
										)}
										<span className="sr-only">Delete Review</span>
									</button>
								)}
							</div>

							<div className="flex items-center gap-2">
								<Stars rating={review.overallRating} readOnly classNames={{ star: "h-6 w-6" }} />
								<span className="pb-3.5 text-sm text-gray-500">
									{review.createdAt ? format(new Date(review.createdAt), "PPP") : ""}
								</span>
							</div>

							{review.reviewAs && (
								<div className="text-sm font-medium text-[#1A2133]">
									<span className="text-[#005EFF]">Review as: </span>
									{review.reviewAs}
								</div>
							)}

							<div className="space-y-1">
								<h4 className="text-base font-bold text-[#1A2133]">{review.title}</h4>
								<p className="text-sm text-gray-800">{review.summary}</p>
							</div>
						</div>

						{expandedReviews[review.id] && (
							<div className="mt-6 space-y-6">
								<div className="space-y-4">
									<p className="text-lg font-semibold text-[#005EFF]">Quick Rating</p>
									{[
										{
											label: "Onboarding Experience & Customer Support",
											value: review.supportRating,
										},
										{ label: "Value for money", value: review.valueRating },
										{ label: "Functionality & ease of use", value: review.functionalityRating },
									].map((item) => (
										<div key={item.label} className="space-y-1 text-[#1A2133]">
											<p className="text-sm font-medium">{item.label}</p>
											<div className="flex items-center gap-4">
												<Stars rating={item.value} readOnly classNames={{ star: "h-6 w-6" }} />
												<span className="text-sm">{item.value}/5</span>
											</div>
										</div>
									))}
								</div>

								{review.usageFrequency && (
									<div className="text-sm">
										<span className="font-semibold text-[#005EFF]">Usage Frequency: </span>
										{review.usageFrequency}
									</div>
								)}

								{(review.startDate || review.endDate) && (
									<div className="space-y-2">
										<p className="text-lg font-semibold text-[#005EFF]">Usage Timeline</p>
										<div className="flex flex-wrap gap-6 text-sm text-[#1A2133]">
											{review.startDate && (
												<p>
													<span className="font-medium">Start Date:</span>{" "}
													{format(new Date(review.startDate), "yyyy-MM-dd")}
												</p>
											)}
											<p>
												<span className="font-medium">End Date:</span>{" "}
												{review.endDate
													? format(new Date(review.endDate), "yyyy-MM-dd")
													: "Ongoing"}
											</p>
										</div>
									</div>
								)}

								{(review.additionalQuestions?.useCase ||
									review.additionalQuestions?.favouriteAspect ||
									review.additionalQuestions?.improvements) && (
									<div className="space-y-2">
										<p className="text-lg font-semibold text-[#005EFF]">Additional Details</p>
										{review.additionalQuestions?.useCase && (
											<p className="text-sm">
												<span className="font-medium">Primary Use Case:</span>{" "}
												{review.additionalQuestions.useCase}
											</p>
										)}
										{review.additionalQuestions?.favouriteAspect && (
											<p className="text-sm">
												<span className="font-medium">Favourite Aspect:</span>{" "}
												{review.additionalQuestions.favouriteAspect}
											</p>
										)}
										{review.additionalQuestions?.improvements && (
											<p className="text-sm">
												<span className="font-medium">Suggested Improvements:</span>{" "}
												{review.additionalQuestions.improvements}
											</p>
										)}
									</div>
								)}
							</div>
						)}

						<p
							onClick={() => toggleReview(review.id)}
							className="mt-6 flex cursor-pointer items-center gap-1 text-sm font-semibold text-[#005EFF]"
						>
							{expandedReviews[review.id] ? "Show Less Review" : "Show Full Review"}
							<ChevronUp
								className={`h-5 w-5 transition-transform duration-200 ${
									expandedReviews[review.id] ? "rotate-180" : ""
								}`}
							/>
						</p>
					</div>
				);
			})}
		</div>
	);
};

export default ReviewCard;
